import { useEffect, useState } from 'react';
import { Button, Modal, Radio, Space, Typography } from 'antd';
import { CheckCircleOutlined } from '@ant-design/icons';

export type NextStepOption = {
  id: string;
  title: string;
  body: string;
  cta: string;
  recommended?: boolean;
};

interface NextStepModalProps {
  open: boolean;
  eyebrow: string;
  description: string;
  options: NextStepOption[];
  onConfirm: (id: string) => void;
}

const NextStepModal = ({
  open,
  eyebrow,
  description,
  options,
  onConfirm,
}: NextStepModalProps) => {
  const [choice, setChoice] = useState(options[0]?.id);
  useEffect(() => {
    if (open) setChoice(options[0]?.id);
  }, [open]);

  const selected = options.find((o) => o.id === choice);

  return (
    <Modal
      open={open}
      // As designed: no dismiss; the client leaves through one of the options.
      closable={false}
      maskClosable={false}
      keyboard={false}
      width={640}
      style={{ top: 64 }}
      footer={
        <Button type="primary" size="large" onClick={() => onConfirm(choice)}>
          {selected?.cta}
        </Button>
      }
    >
      <Space direction="vertical" size={8} style={{ width: '100%' }}>
        <Typography.Text type="success" strong>
          <CheckCircleOutlined /> {eyebrow}
        </Typography.Text>
        <Typography.Title level={3} style={{ margin: 0 }}>
          What would you like to do next?
        </Typography.Title>
        <Typography.Paragraph type="secondary">
          {description}
        </Typography.Paragraph>
        <Radio.Group
          value={choice}
          onChange={(e) => setChoice(e.target.value)}
          style={{ width: '100%' }}
          aria-label="Next step"
        >
          <Space direction="vertical" size={12} style={{ width: '100%' }}>
            {options.map((o) => (
              <Radio
                key={o.id}
                value={o.id}
                style={{
                  width: '100%',
                  padding: '16px 18px',
                  borderRadius: 10,
                  border: `2px solid ${o.id === choice ? '#003862' : '#e1e1e1'}`,
                  background: o.id === choice ? '#f5f9fc' : '#ffffff',
                }}
              >
                <Space direction="vertical" size={4}>
                  <span>
                    <Typography.Text strong>{o.title}</Typography.Text>
                    {o.recommended && (
                      <span
                        style={{
                          marginLeft: 8,
                          padding: '2px 10px',
                          borderRadius: 999,
                          fontSize: 12,
                          fontWeight: 500,
                          color: '#003862',
                          background: '#e6eef5',
                        }}
                      >
                        Recommended
                      </span>
                    )}
                  </span>
                  <Typography.Text type="secondary">{o.body}</Typography.Text>
                </Space>
              </Radio>
            ))}
          </Space>
        </Radio.Group>
      </Space>
    </Modal>
  );
};

export default NextStepModal;
